import React, { useEffect, useState, useCallback } from 'react';
import { getPosts, moderatePost, deletePost, createCommunityPost } from '../api';

export default function PostsPage() {
  const [posts, setPosts] = useState([]);
  const [pagination, setPagination] = useState({ total: 0, limit: 50, offset: 0 });
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [showComposer, setShowComposer] = useState(false);
  const [newPost, setNewPost] = useState({ type: 'announcement', produceName: '', content: '' });
  const [posting, setPosting] = useState(false);

  const fetchPosts = useCallback(async (offset = 0) => {
    setLoading(true);
    try {
      const params = { limit: '50', offset: String(offset) };
      if (search) params.search = search;
      if (statusFilter) params.status = statusFilter;

      const data = await getPosts(params);
      setPosts(data.posts);
      setPagination(data.pagination);
    } catch {
      // handled
    } finally {
      setLoading(false);
    }
  }, [search, statusFilter]);

  useEffect(() => {
    fetchPosts(0);
  }, [fetchPosts]);

  const handleToggleVisibility = async (post) => {
    try {
      await moderatePost(post.id, !post.isActive);
      setPosts((prev) =>
        prev.map((p) => (p.id === post.id ? { ...p, isActive: !p.isActive } : p)),
      );
    } catch {
      // handled
    }
  };

  const handleDelete = async (post) => {
    if (!window.confirm(`Permanently delete this post by ${post.userName}? This cannot be undone.`)) {
      return;
    }

    try {
      await deletePost(post.id);
      setPosts((prev) => prev.filter((p) => p.id !== post.id));
    } catch {
      // handled
    }
  };

  const handleCreatePost = async (e) => {
    e.preventDefault();
    if (!newPost.content.trim()) return;
    setPosting(true);
    try {
      await createCommunityPost(newPost);
      setNewPost({ type: 'announcement', produceName: '', content: '' });
      setShowComposer(false);
      fetchPosts(0);
    } catch (err) {
      alert(err.message || 'Failed to create post');
    } finally {
      setPosting(false);
    }
  };

  return (
    <div>
      <div className="page-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
        <div>
          <h1>Community Posts</h1>
          <p>Moderate community content</p>
        </div>
        <button className="btn btn-primary" onClick={() => setShowComposer(!showComposer)}>
          {showComposer ? 'Cancel' : '+ New Post'}
        </button>
      </div>

      {showComposer && (
        <div className="card" style={{ marginBottom: 20, padding: 20 }}>
          <h3 style={{ margin: '0 0 16px', fontSize: 16, fontWeight: 600 }}>Create Community Post</h3>
          <form onSubmit={handleCreatePost} style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
            <div style={{ display: 'flex', gap: 12 }}>
              <select
                className="filter-select"
                value={newPost.type}
                onChange={(e) => setNewPost({ ...newPost, type: e.target.value })}
                style={{ flex: 1 }}
              >
                <option value="announcement">Announcement</option>
                <option value="tip">Tip</option>
                <option value="seasonal">Seasonal</option>
                <option value="deal">Deal</option>
              </select>
              <input
                className="search-input"
                placeholder="Produce name (optional)"
                value={newPost.produceName}
                onChange={(e) => setNewPost({ ...newPost, produceName: e.target.value })}
                style={{ flex: 1 }}
              />
            </div>
            <textarea
              className="search-input"
              placeholder="Write your post content..."
              value={newPost.content}
              onChange={(e) => setNewPost({ ...newPost, content: e.target.value })}
              rows={3}
              style={{ resize: 'vertical', fontFamily: 'inherit' }}
            />
            <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
              <button className="btn btn-primary" type="submit" disabled={posting || !newPost.content.trim()}>
                {posting ? 'Posting...' : 'Publish Post'}
              </button>
            </div>
          </form>
        </div>
      )}

      <form className="toolbar" onSubmit={(e) => { e.preventDefault(); fetchPosts(0); }}>
        <input
          className="search-input"
          placeholder="Search posts..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />
        <select className="filter-select" value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)}>
          <option value="">All posts</option>
          <option value="active">Active</option>
          <option value="hidden">Hidden</option>
        </select>
      </form>

      {loading ? (
        <div className="loading">Loading posts...</div>
      ) : posts.length === 0 ? (
        <div className="empty-state"><p>No posts found.</p></div>
      ) : (
        <>
          <div className="card">
            <div className="table-container">
              <table>
                <thead>
                  <tr>
                    <th>Author</th>
                    <th>Type</th>
                    <th>Content</th>
                    <th>Produce</th>
                    <th>Likes</th>
                    <th>Status</th>
                    <th>Date</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {posts.map((post) => (
                    <tr key={post.id}>
                      <td style={{ fontWeight: 500, whiteSpace: 'nowrap' }}>{post.userName}</td>
                      <td>
                        <span className="badge badge-gray">{post.type}</span>
                      </td>
                      <td style={{ maxWidth: 300, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                        {post.content}
                      </td>
                      <td style={{ fontSize: 13 }}>{post.produceName || '—'}</td>
                      <td style={{ fontSize: 13 }}>{post.likes}</td>
                      <td>
                        <span className={`badge ${post.isActive ? 'badge-green' : 'badge-red'}`}>
                          {post.isActive ? 'Active' : 'Hidden'}
                        </span>
                      </td>
                      <td style={{ fontSize: 13, color: '#888', whiteSpace: 'nowrap' }}>
                        {new Date(post.createdAt).toLocaleDateString('en-ZA')}
                      </td>
                      <td style={{ whiteSpace: 'nowrap' }}>
                        <button
                          className={`btn btn-sm ${post.isActive ? 'btn-secondary' : 'btn-primary'}`}
                          onClick={() => handleToggleVisibility(post)}
                          style={{ marginRight: 6 }}
                        >
                          {post.isActive ? 'Hide' : 'Restore'}
                        </button>
                        <button
                          className="btn btn-sm btn-danger"
                          onClick={() => handleDelete(post)}
                        >
                          Delete
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          <div className="pagination">
            <span>
              Showing {pagination.offset + 1}–{Math.min(pagination.offset + pagination.limit, pagination.total)} of {pagination.total}
            </span>
            <div style={{ display: 'flex', gap: 8 }}>
              <button
                className="btn btn-secondary btn-sm"
                disabled={pagination.offset === 0}
                onClick={() => fetchPosts(Math.max(0, pagination.offset - pagination.limit))}
              >
                Previous
              </button>
              <button
                className="btn btn-secondary btn-sm"
                disabled={!pagination.hasMore}
                onClick={() => fetchPosts(pagination.offset + pagination.limit)}
              >
                Next
              </button>
            </div>
          </div>
        </>
      )}
    </div>
  );
}
